import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '@/hooks/redux'
import { logout } from '@/store/slices/authSlice'
import { apiUrl } from '@/utils/api'

interface CompletionAuthGuardProps {
  children: React.ReactNode
}

export const CompletionAuthGuard: React.FC<CompletionAuthGuardProps> = ({ children }) => {
  const { isAuthenticated, candidateId, token } = useAppSelector(state => state.auth)
  const dispatch = useAppDispatch()
  const [isValidating, setIsValidating] = useState(true)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    const validateCompletedSession = async () => {
      // First check if basic auth info is present
      if (!isAuthenticated || !candidateId || !token) {
        console.log('No authentication data found, redirecting to welcome')
        setIsValidating(false)
        setIsValid(false)
        return
      }

      try {
        // Check if there's a completed interview session
        const sessionId = localStorage.getItem('interview-session-id')
        if (!sessionId) {
          console.log('No interview session ID found, redirecting to welcome')
          dispatch(logout())
          setIsValidating(false)
          setIsValid(false)
          return
        }

        // Validate the session with the backend
        const response = await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/validate`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const sessionData = await response.json()
          
          // Only allow access if session exists (regardless of status)
          // This allows viewing completion page for active or completed interviews
          if (sessionData.status) {
            console.log('Valid session found for completion page')
            setIsValid(true)
          } else {
            console.log('Invalid session for completion page')
            dispatch(logout())
            localStorage.removeItem('interview-session-id')
            setIsValid(false)
          }
        } else {
          console.log('Session validation failed for completion page')
          dispatch(logout())
          localStorage.removeItem('interview-session-id')
          setIsValid(false)
        }
      } catch (error) {
        console.error('Error validating completion session:', error)
        dispatch(logout())
        localStorage.removeItem('interview-session-id')
        setIsValid(false)
      }

      setIsValidating(false)
    }

    validateCompletedSession()
  }, [isAuthenticated, candidateId, token, dispatch])

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-snwolley-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating session...</p>
        </div>
      </div>
    )
  }

  // Redirect if not valid
  if (!isValid) {
    return <Navigate to="/welcome" replace />
  }

  // Render children if valid
  return <>{children}</>
}